import {
  ORG_ROLE_DESCRIPTIONS,
  ORG_ROLE_LABELS,
  type Invitation,
  type Membership,
} from '@hvac/contracts';
import { ORG_ROLES, type OrgRole } from '@hvac/core';
import { Alert, Button, Card, CardBody, CardHeader, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  changeMemberRole,
  inviteMember,
  invitationsQuery,
  membersQuery,
  peopleKeys,
  revokeMember,
  withdrawInvitation,
} from '../../shared/api/membership-queries.js';
import { useAuth } from '../../shared/auth/auth-context.js';

/**
 * Who can see these books, and inviting the next person.
 *
 * An invitation is a link. It is shown once, here, for the inviter to send by
 * whatever means they already use with that person; the database keeps only
 * its hash. Whoever opens it must sign in as the invited address, so a link
 * forwarded to the wrong person grants nothing.
 */

const INVITABLE: readonly OrgRole[] = ORG_ROLES;

export function PeopleCard({
  organizationId,
  myRole,
  canInvite,
}: {
  organizationId: string;
  myRole: OrgRole;
  canInvite: boolean;
}) {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const members = useQuery(membersQuery(organizationId));
  const invitations = useQuery({ ...invitationsQuery(organizationId), enabled: canInvite });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('bookkeeper');
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: peopleKeys.all(organizationId) });

  const invite = useMutation({
    mutationFn: () => inviteMember(organizationId, { email: email.trim().toLowerCase(), role }),
    onSuccess: (created) => {
      setLink({ email: created.invitation.email, url: created.inviteUrl });
      setCopied(false);
      setEmail('');
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The invitation was not created.'),
  });

  const withdraw = useMutation({
    mutationFn: (invitationId: string) => withdrawInvitation(organizationId, invitationId),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The invitation was not withdrawn.'),
  });

  const reassign = useMutation({
    mutationFn: (input: { membershipId: string; role: OrgRole }) =>
      changeMemberRole(organizationId, input.membershipId, { role: input.role }),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The role was not changed.'),
  });

  const remove = useMutation({
    mutationFn: (membershipId: string) => revokeMember(organizationId, membershipId),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Access was not removed.'),
  });

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const active = (members.data ?? []).filter((member) => member.status === 'active');
  const pending = (invitations.data ?? []).filter((item) => item.status === 'pending');
  const roleOptions = INVITABLE.filter(
    (option) => myRole === 'owner' || rank(myRole) > rank(option),
  );

  return (
    <Card>
      <CardHeader
        title="People"
        description={
          members.data === undefined
            ? 'Reading…'
            : `${String(active.length)} ${active.length === 1 ? 'person has' : 'people have'} access to these books${pending.length === 0 ? '.' : `, and ${String(pending.length)} invitation${pending.length === 1 ? ' is' : 's are'} waiting.`}`
        }
      />
      <CardBody className="flex flex-col gap-5">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {members.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Person</th>
                <th className="py-1 font-medium">Role</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {active.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isMe={member.profile.userId === auth.userId}
                  canManage={
                    canInvite &&
                    member.profile.userId !== auth.userId &&
                    (myRole === 'owner' || rank(myRole) > rank(member.role))
                  }
                  roleOptions={roleOptions}
                  onRole={(next) => reassign.mutate({ membershipId: member.id, role: next })}
                  onRemove={() => remove.mutate(member.id)}
                />
              ))}
            </tbody>
          </table>
        )}

        {!canInvite ? null : (
          <>
            {pending.length === 0 ? null : (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium uppercase text-content-muted">
                  Waiting to accept
                </p>
                {pending.map((item) => (
                  <PendingRow
                    key={item.id}
                    invitation={item}
                    onWithdraw={() => withdraw.mutate(item.id)}
                  />
                ))}
              </div>
            )}

            {link === null ? null : (
              <Alert tone="success" title={`Invitation for ${link.email} is ready`}>
                <p>
                  Send this link to them however you already talk: text, email, chat. It works once,
                  for seven days, and only for that address.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="max-w-full break-all rounded bg-surface-sunken px-2 py-1 text-xs">
                    {link.url}
                  </code>
                  <Button size="sm" variant="secondary" onClick={() => void copy(link.url)}>
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-content-muted">
                  This is the only time the link is shown. If it is lost, withdraw the invitation
                  and send a new one.
                </p>
              </Alert>
            )}

            <div className="grid gap-3 rounded-[var(--radius-control)] border border-border p-3 sm:grid-cols-[1fr_14rem_auto] sm:items-end">
              <Field label="Invite by email">
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="megan@firm.ca"
                  />
                )}
              </Field>
              <Field label="As" hint={ORG_ROLE_DESCRIPTIONS[role]}>
                {({ inputProps }) => (
                  <select
                    {...inputProps}
                    value={role}
                    onChange={(event) => setRole(event.target.value as OrgRole)}
                    className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                  >
                    {roleOptions.map((option) => (
                      <option key={option} value={option}>
                        {ORG_ROLE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Button
                onClick={() => invite.mutate()}
                isLoading={invite.isPending}
                disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())}
              >
                Create invitation
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function MemberRow({
  member,
  isMe,
  canManage,
  roleOptions,
  onRole,
  onRemove,
}: {
  member: Membership;
  isMe: boolean;
  canManage: boolean;
  roleOptions: readonly OrgRole[];
  onRole: (role: OrgRole) => void;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <tr className="border-t border-border">
      <td className="py-2">
        <div className="font-medium">
          {member.profile.fullName ?? member.profile.email}
          {isMe ? <span className="text-content-muted"> (you)</span> : null}
        </div>
        {member.profile.fullName === null ? null : (
          <div className="text-xs text-content-muted">{member.profile.email}</div>
        )}
      </td>
      <td className="py-2">
        {canManage ? (
          <select
            aria-label={`Role for ${member.profile.email}`}
            value={member.role}
            onChange={(event) => onRole(event.target.value as OrgRole)}
            className="h-9 rounded-[var(--radius-control)] border border-border-strong bg-surface px-2 text-sm"
          >
            {[...new Set([member.role, ...roleOptions])].map((option) => (
              <option key={option} value={option}>
                {ORG_ROLE_LABELS[option]}
              </option>
            ))}
          </select>
        ) : (
          ORG_ROLE_LABELS[member.role]
        )}
      </td>
      <td className="py-2 text-right">
        {!canManage ? null : confirming ? (
          <span className="inline-flex items-center gap-2">
            <Button size="sm" variant="danger" onClick={onRemove}>
              Remove access
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Keep
            </Button>
          </span>
        ) : (
          <button
            type="button"
            className="text-xs text-content-muted hover:text-danger"
            onClick={() => setConfirming(true)}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

function PendingRow({
  invitation,
  onWithdraw,
}: {
  invitation: Invitation;
  onWithdraw: () => void;
}) {
  const expires = invitation.expiresAt.slice(0, 10);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-control)] bg-surface-sunken px-3 py-2 text-sm">
      <span>
        {invitation.email}{' '}
        <span className="text-content-muted">as {ORG_ROLE_LABELS[invitation.role]}</span>
        <span className="text-xs text-content-muted"> · link valid until {expires}</span>
      </span>
      <button
        type="button"
        className="text-xs text-content-muted hover:text-danger"
        onClick={onWithdraw}
      >
        Withdraw
      </button>
    </div>
  );
}

const RANK: Readonly<Record<OrgRole, number>> = {
  owner: 100,
  admin: 80,
  bookkeeper: 60,
  accountant: 50,
  technician: 20,
  viewer: 10,
};
function rank(role: OrgRole): number {
  return RANK[role];
}
